import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { Controller, useForm } from "react-hook-form";
import {
  Close,
  ContentCopy,
  ExpandMore,
  HelpOutline,
  Lock,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState, useRef } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import PropTypes from "prop-types";
import Scrollbar from "../../../components/scrollbar/Scrollbar";
import {
  useGetPlansQuery,
  useUpdatePlanMutation,
} from "../../../redux/services/plans.service";
import { useGetCountriesQuery } from "../../../redux/services/country.service";

// Helper component for field descriptions
const FieldDescription = ({ text }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ display: "block", mt: 0.5, mb: 1 }}
  >
    {text}
  </Typography>
);

FieldDescription.propTypes = {
  text: PropTypes.string.isRequired,
};

// Constants
const PLAN_TYPES = [
  {
    value: "RAZORPAY",
    label: "Razorpay (Recurring)",
    description: "Automatic recurring payments via Razorpay payment gateway",
  },
  {
    value: "RAZORPAY_ONE_TIME",
    label: "Razorpay (One-Time)",
    description: "Single payment via Razorpay, no automatic renewal",
  },
  {
    value: "CUSTOM",
    label: "Custom",
    description: "Custom plan assigned manually by administrators",
  },
  {
    value: "DEVICE",
    label: "Device",
    description: "Plan tied to a specific device",
  },
];

const PERIODS = [
  { value: "daily", label: "Daily", description: "Billed every day" },
  { value: "weekly", label: "Weekly", description: "Billed every week" },
  { value: "monthly", label: "Monthly", description: "Billed every month" },
  { value: "yearly", label: "Yearly", description: "Billed every year" },
];

const CURRENCIES = [
  { value: "INR", label: "INR (₹) - Indian Rupee" },
  { value: "USD", label: "USD ($) - US Dollar" },
];

const APPLICATION_TYPES = [
  {
    value: "ONLINE",
    label: "Online Only",
    description: "Plan works only with internet connection",
  },
  {
    value: "OFFLINE",
    label: "Offline Capable",
    description: "Plan includes offline features",
  },
];

const FEATURES = [
  {
    key: "smartEye",
    label: "Smart Eye",
    description: "AI-powered visual assistance",
  },
  {
    key: "smartImage",
    label: "Smart Image",
    description: "Image recognition and analysis",
  },
  {
    key: "aiChat",
    label: "AI Chat",
    description: "Conversational AI assistant",
  },
  {
    key: "mira",
    label: "MIRA",
    description: "Multi-modal intelligent assistant",
  },
  { key: "document", label: "Document AI", description: "Document processing" },
  {
    key: "text",
    label: "Text Recognition",
    description: "Text recognition and processing",
  },
  {
    key: "currency",
    label: "Currency",
    description: "Currency recognition feature",
  },
];

EditPlanForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  openRow: PropTypes.object,
};

export default function EditPlanForm({ open, onClose, openRow }) {
  const { enqueueSnackbar } = useSnackbar();
  const [expandedSection, setExpandedSection] = useState("basic");
  const [selectedPlanForLimits, setSelectedPlanForLimits] = useState(null);
  const [selectedPlanForFeatures, setSelectedPlanForFeatures] = useState(null);

  // Check if plan is in pending state
  const isPending =
    openRow?.status === "pending" || openRow?.status === "PENDING";

  // JSON editor state
  const [jsonEditorValue, setJsonEditorValue] = useState("");
  const [jsonError, setJsonError] = useState("");

  // Store initial values to compare for dirty tracking
  const initialValuesRef = useRef({});

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm();

  // Populate form when drawer opens with plan data
  useEffect(() => {
    if (open && openRow) {
      const initialValues = {
        name: openRow?.name || "",
        description: openRow?.description || "",
        razorpayDescription: openRow?.razorpayDescription || "",
        type: openRow?.type || "RAZORPAY",
        isRecurring: openRow?.isRecurring ?? true,
        period: openRow?.period || "monthly",
        periodName: openRow?.periodName || "",
        interval: openRow?.interval || 1,
        amount: openRow?.amount,
        currency: openRow?.currency || "INR",
        countryCode: openRow?.countryCode || "",
        linkedPlanId: openRow?.linkedPlanId || "",
        offerValidTill: openRow?.offerValidTill || null,
        applicationPlanType: openRow?.applicationPlanType || "ONLINE",
        durationDays: openRow?.durationDays,
        // Usage Limits - Smart Eye
        smarteyeTokenLimit: openRow?.smarteyeTokenLimit,
        smarteyeConversationLimit: openRow?.smarteyeConversationLimit,
        smarteyeTokenLimitShort: openRow?.smarteyeTokenLimitShort,
        // Usage Limits - Smart Eye Currency
        smarteyeCurrencyTokenLimit: openRow?.smarteyeCurrencyTokenLimit,
        smarteyeCurrencyConversationLimit:
          openRow?.smarteyeCurrencyConversationLimit,
        // Usage Limits - Smart Image
        smartImageTokenLimit: openRow?.smartImageTokenLimit,
        smartImageConversationLimit: openRow?.smartImageConversationLimit,
        smartImageTokenLimitShort: openRow?.smartImageTokenLimitShort,
        // Usage Limits - AI Chat
        aiChatTokenLimit: openRow?.aiChatTokenLimit,
        aiChatConversationLimit: openRow?.aiChatConversationLimit,
        // Usage Limits - MIRA
        miraTokenLimit: openRow?.miraTokenLimit,
        miraConversationLimit: openRow?.miraConversationLimit,
        // Usage Limits - Document AI
        documentAskQnaLimit: openRow?.documentAskQnaLimit,
        documentSummaryLimit: openRow?.documentSummaryLimit,
        documentGenQnaLimit: openRow?.documentGenQnaLimit,
        // Usage Limits - Other
        textLimit: openRow?.textLimit,
        documentPagesLimit: openRow?.documentPagesLimit,
        // Features
        availableFeatures: openRow?.availableFeatures || {},
      };

      // Store initial values for dirty comparison
      initialValuesRef.current = JSON.parse(JSON.stringify(initialValues));

      reset(initialValues);

      // Populate JSON editor with existing features
      if (openRow?.availableFeatures) {
        setJsonEditorValue(JSON.stringify(openRow.availableFeatures, null, 2));
      } else {
        setJsonEditorValue("");
      }

      // Reset copy selections
      setSelectedPlanForLimits(null);
      setSelectedPlanForFeatures(null);
    }
  }, [open, openRow, reset]);

  const [updatePlan, { isLoading: isUpdating }] = useUpdatePlanMutation();

  // Watch values for conditional rendering
  const watchType = watch("type");
  const watchLinkedPlanId = watch("linkedPlanId");

  // Fetch countries
  const { data: countriesData, isLoading: isCountriesLoading } =
    useGetCountriesQuery(null, {});
  const countries = countriesData?.data || [];

  // Fetch existing plans for linked plan dropdown and copying
  const { data: plansData, isLoading: isPlansLoading } = useGetPlansQuery(
    null,
    {},
  );
  const existingPlans = plansData?.data || [];

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  // Helper function to check if a value has changed
  const hasValueChanged = (key, currentValue) => {
    const initialValue = initialValuesRef.current[key];

    // Handle special case for availableFeatures (object comparison)
    if (key === "availableFeatures") {
      return (
        JSON.stringify(currentValue || {}) !==
        JSON.stringify(initialValue || {})
      );
    }

    // Handle special case for offerValidTill (date comparison)
    if (key === "offerValidTill") {
      const currentDate = currentValue
        ? moment(currentValue).format("YYYY-MM-DD")
        : null;
      const initialDate = initialValue
        ? moment(initialValue).format("YYYY-MM-DD")
        : null;
      return currentDate !== initialDate;
    }

    // Handle numbers - treat NaN, undefined, null, and "" as equivalent
    if (typeof currentValue === "number" || typeof initialValue === "number") {
      const currentNum =
        currentValue === "" ||
        currentValue === null ||
        currentValue === undefined ||
        Number.isNaN(currentValue)
          ? null
          : Number(currentValue);
      const initialNum =
        initialValue === "" ||
        initialValue === null ||
        initialValue === undefined ||
        Number.isNaN(initialValue)
          ? null
          : Number(initialValue);
      return currentNum !== initialNum;
    }

    // Default comparison
    return currentValue !== initialValue;
  };

  const handleFormSubmit = async (values) => {
    try {
      // Start with just the id
      const payload = {
        id: openRow.id,
      };

      // List of all editable usage limit fields
      const usageLimitFields = [
        "smarteyeTokenLimit",
        "smarteyeConversationLimit",
        "smarteyeTokenLimitShort",
        "smarteyeCurrencyTokenLimit",
        "smarteyeCurrencyConversationLimit",
        "smartImageTokenLimit",
        "smartImageConversationLimit",
        "smartImageTokenLimitShort",
        "aiChatTokenLimit",
        "aiChatConversationLimit",
        "miraTokenLimit",
        "miraConversationLimit",
        "documentAskQnaLimit",
        "documentSummaryLimit",
        "documentGenQnaLimit",
        "textLimit",
        "documentPagesLimit",
        "durationDays",
      ];

      // Check and add changed usage limit fields
      usageLimitFields.forEach((field) => {
        if (hasValueChanged(field, values[field])) {
          // Only add if value is not empty/null/undefined/NaN
          if (
            values[field] !== undefined &&
            values[field] !== null &&
            values[field] !== "" &&
            !Number.isNaN(values[field])
          ) {
            payload[field] = Number(values[field]);
          }
        }
      });

      // Check and add changed features
      if (hasValueChanged("availableFeatures", values.availableFeatures)) {
        payload.availableFeatures = values.availableFeatures;
      }

      // Check and add changed pricing fields (only if pending)
      if (isPending) {
        if (hasValueChanged("amount", values.amount)) {
          if (
            values.amount !== undefined &&
            values.amount !== null &&
            values.amount !== ""
          ) {
            payload.amount = Number(values.amount);
          }
        }
        if (hasValueChanged("currency", values.currency)) {
          payload.currency = values.currency;
        }
        if (hasValueChanged("countryCode", values.countryCode)) {
          payload.countryCode = values.countryCode;
        }
      }

      // Check and add changed offer configuration
      if (hasValueChanged("linkedPlanId", values.linkedPlanId)) {
        payload.linkedPlanId = values.linkedPlanId || null;
      }
      if (hasValueChanged("offerValidTill", values.offerValidTill)) {
        payload.offerValidTill = values.offerValidTill
          ? moment(values.offerValidTill).format("YYYY-MM-DD")
          : null;
      }

      // Check if any changes were made (payload should have more than just id)
      if (Object.keys(payload).length === 1) {
        enqueueSnackbar("No changes were made", {
          variant: "info",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        return;
      }

      const result = await updatePlan(payload);

      if (result?.data?.success) {
        enqueueSnackbar("Plan updated successfully!", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        reset();
        onClose();
      } else {
        enqueueSnackbar(result?.data?.message || "Failed to update plan", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      }
    } catch (error) {
      enqueueSnackbar("Error updating plan. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
  };

  // Copy usage limits from selected plan
  const handleCopyUsageLimits = (plan) => {
    if (!plan) return;

    const setLimitValue = (fieldName, value) => {
      if (value !== undefined && value !== null) {
        setValue(fieldName, value);
      }
    };

    // Smart Eye limits
    setLimitValue("smarteyeTokenLimit", plan.smarteyeTokenLimit);
    setLimitValue("smarteyeConversationLimit", plan.smarteyeConversationLimit);
    setLimitValue("smarteyeTokenLimitShort", plan.smarteyeTokenLimitShort);

    // Smart Eye Currency limits
    setLimitValue(
      "smarteyeCurrencyTokenLimit",
      plan.smarteyeCurrencyTokenLimit,
    );
    setLimitValue(
      "smarteyeCurrencyConversationLimit",
      plan.smarteyeCurrencyConversationLimit,
    );

    // Smart Image limits
    setLimitValue("smartImageTokenLimit", plan.smartImageTokenLimit);
    setLimitValue(
      "smartImageConversationLimit",
      plan.smartImageConversationLimit,
    );
    setLimitValue("smartImageTokenLimitShort", plan.smartImageTokenLimitShort);

    // AI Chat limits
    setLimitValue("aiChatTokenLimit", plan.aiChatTokenLimit);
    setLimitValue("aiChatConversationLimit", plan.aiChatConversationLimit);

    // MIRA limits
    setLimitValue("miraTokenLimit", plan.miraTokenLimit);
    setLimitValue("miraConversationLimit", plan.miraConversationLimit);

    // Document AI limits
    setLimitValue("documentAskQnaLimit", plan.documentAskQnaLimit);
    setLimitValue("documentSummaryLimit", plan.documentSummaryLimit);
    setLimitValue("documentGenQnaLimit", plan.documentGenQnaLimit);

    // Other limits
    setLimitValue("textLimit", plan.textLimit);
    setLimitValue("documentPagesLimit", plan.documentPagesLimit);
    setLimitValue("durationDays", plan.durationDays);

    setSelectedPlanForLimits(plan);
    enqueueSnackbar(`Usage limits copied from "${plan.name}"`, {
      variant: "success",
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
    });
  };

  // Copy feature access from selected plan
  const handleCopyFeatureAccess = (plan) => {
    if (!plan) return;

    if (plan.availableFeatures) {
      const featuresCopy = JSON.parse(JSON.stringify(plan.availableFeatures));
      setValue("availableFeatures", featuresCopy, { shouldDirty: true });
      setJsonEditorValue(JSON.stringify(featuresCopy, null, 2));
      setJsonError("");
    }

    setSelectedPlanForFeatures(plan);
    enqueueSnackbar(`Feature access configuration copied from "${plan.name}"`, {
      variant: "success",
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
    });
  };

  // Apply JSON changes
  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonEditorValue);
      setValue("availableFeatures", parsed, { shouldDirty: true });
      setJsonError("");
      enqueueSnackbar("Feature access configuration updated from JSON", {
        variant: "success",
      });
    } catch (error) {
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  };

  // Format JSON
  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(jsonEditorValue);
      setJsonEditorValue(JSON.stringify(parsed, null, 2));
      setJsonError("");
    } catch (error) {
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  };

  // Refresh JSON from form
  const handleRefreshJson = () => {
    const currentFeatures = watch("availableFeatures") || {};
    setJsonEditorValue(JSON.stringify(currentFeatures, null, 2));
    setJsonError("");
  };

  // Helper to render disabled indicator
  const DisabledBadge = () => (
    <Tooltip title="This field cannot be edited">
      <Lock fontSize="small" sx={{ ml: 1, color: "text.disabled" }} />
    </Tooltip>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 600, md: 700 },
          border: "none",
          overflow: "hidden",
        },
      }}
    >
      <Scrollbar>
        <Stack spacing={2} sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Edit Plan
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Editable sections:</strong> Usage Limits, Feature Access
              Configuration, and Offer Configuration.
              {isPending && (
                <>
                  {" "}
                  <strong>Pricing</strong> is also editable because this plan is
                  in pending state.
                </>
              )}
            </Typography>
          </Alert>

          <Divider />

          <form onSubmit={handleSubmit(handleFormSubmit)}>
            {/* Section 1: Basic Information - DISABLED */}
            <Accordion
              expanded={expandedSection === "basic"}
              onChange={handleAccordionChange("basic")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="bold">
                    📝 Basic Information
                  </Typography>
                  <DisabledBadge />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Box>
                    <TextField
                      fullWidth
                      label="Plan Name"
                      {...register("name")}
                      disabled
                      InputProps={{
                        sx: { bgcolor: "action.disabledBackground" },
                      }}
                    />
                    <FieldDescription text="This is the name users will see when choosing a plan." />
                  </Box>

                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      {...register("description")}
                      disabled
                      InputProps={{
                        sx: { bgcolor: "action.disabledBackground" },
                      }}
                    />
                    <FieldDescription text="A detailed description of the plan's benefits and features." />
                  </Box>

                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Razorpay Description"
                      {...register("razorpayDescription")}
                      disabled
                      InputProps={{
                        sx: { bgcolor: "action.disabledBackground" },
                      }}
                    />
                    <FieldDescription text="This text appears on the Razorpay checkout page." />
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Section 2: Plan Type & Billing - DISABLED */}
            <Accordion
              expanded={expandedSection === "billing"}
              onChange={handleAccordionChange("billing")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="bold">
                    💳 Plan Type & Billing
                  </Typography>
                  <DisabledBadge />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  {/* Plan Type */}
                  <Box>
                    <FormControl fullWidth disabled>
                      <InputLabel>Plan Type</InputLabel>
                      <Controller
                        name="type"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Plan Type"
                            disabled
                            sx={{ bgcolor: "action.disabledBackground" }}
                          >
                            {PLAN_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                    <FieldDescription text="How this plan is processed." />
                  </Box>

                  {/* Recurring Toggle - Only for Razorpay */}
                  {watchType === "RAZORPAY" && (
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography variant="subtitle2">
                            Recurring Subscription
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Automatically charge users at each billing cycle
                          </Typography>
                        </Box>
                        <Controller
                          name="isRecurring"
                          control={control}
                          render={({ field }) => (
                            <Switch {...field} checked={field.value} disabled />
                          )}
                        />
                      </Stack>
                    </Paper>
                  )}

                  {/* Period Selection - For Razorpay */}
                  {(watchType === "RAZORPAY" ||
                    watchType === "RAZORPAY_ONE_TIME") && (
                    <>
                      <Box>
                        <FormControl fullWidth disabled>
                          <InputLabel>Billing Period</InputLabel>
                          <Controller
                            name="period"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Billing Period"
                                disabled
                                sx={{ bgcolor: "action.disabledBackground" }}
                              >
                                {PERIODS.map((period) => (
                                  <MenuItem
                                    key={period.value}
                                    value={period.value}
                                  >
                                    {period.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                        </FormControl>
                        <FieldDescription text="How often users will be billed." />
                      </Box>

                      <Box>
                        <TextField
                          fullWidth
                          label="Period Display Name"
                          {...register("periodName")}
                          disabled
                          InputProps={{
                            sx: { bgcolor: "action.disabledBackground" },
                          }}
                        />
                        <FieldDescription text="A friendly name shown to users." />
                      </Box>

                      <Box>
                        <TextField
                          fullWidth
                          type="number"
                          label="Interval"
                          {...register("interval", { valueAsNumber: true })}
                          disabled
                          InputProps={{
                            sx: { bgcolor: "action.disabledBackground" },
                          }}
                        />
                        <FieldDescription text="How many periods between each billing." />
                      </Box>
                    </>
                  )}

                  {/* Duration Days - For Custom/Device */}
                  {(watchType === "CUSTOM" || watchType === "DEVICE") && (
                    <Box>
                      <TextField
                        fullWidth
                        type="number"
                        label="Duration (Days)"
                        {...register("durationDays", { valueAsNumber: true })}
                        disabled
                        InputProps={{
                          sx: { bgcolor: "action.disabledBackground" },
                        }}
                      />
                      <FieldDescription text="How many days the subscription will last." />
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Section 3: Pricing - EDITABLE when pending */}
            <Accordion
              expanded={expandedSection === "pricing"}
              onChange={handleAccordionChange("pricing")}
              sx={{
                mb: 2,
                ...(isPending && {
                  border: "2px solid",
                  borderColor: "primary.main",
                  borderRadius: 1,
                }),
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={isPending ? { bgcolor: "primary.50" } : {}}
              >
                <Stack direction="row" alignItems="center">
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color={isPending ? "primary.main" : "inherit"}
                  >
                    💰 Pricing {isPending ? "(Editable - Pending Plan)" : ""}
                  </Typography>
                  {!isPending && <DisabledBadge />}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {isPending && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This plan is in pending state. You can edit pricing fields.
                  </Alert>
                )}
                <Stack spacing={2}>
                  <Box>
                    <TextField
                      fullWidth
                      type="number"
                      label="Amount"
                      {...register("amount", {
                        valueAsNumber: true,
                      })}
                      disabled={!isPending}
                      InputProps={{
                        sx: !isPending
                          ? { bgcolor: "action.disabledBackground" }
                          : {},
                        startAdornment: (
                          <InputAdornment position="start">
                            {watch("currency") === "INR" ? "₹" : "$"}
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FieldDescription text="Price in the smallest currency unit." />
                  </Box>

                  <Box>
                    <FormControl fullWidth disabled={!isPending}>
                      <InputLabel>Currency</InputLabel>
                      <Controller
                        name="currency"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Currency"
                            disabled={!isPending}
                            sx={
                              !isPending
                                ? { bgcolor: "action.disabledBackground" }
                                : {}
                            }
                          >
                            {CURRENCIES.map((curr) => (
                              <MenuItem key={curr.value} value={curr.value}>
                                {curr.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                    <FieldDescription text="The currency in which users will be charged." />
                  </Box>

                  <Box>
                    <FormControl fullWidth disabled={!isPending}>
                      <InputLabel>Country</InputLabel>
                      <Controller
                        name="countryCode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Country"
                            disabled={!isPending}
                            sx={
                              !isPending
                                ? { bgcolor: "action.disabledBackground" }
                                : {}
                            }
                          >
                            {isCountriesLoading ? (
                              <MenuItem disabled>
                                <CircularProgress size={24} />
                              </MenuItem>
                            ) : (
                              countries.map((country) => (
                                <MenuItem
                                  key={country.id}
                                  value={country.countryMobileCode}
                                >
                                  {country.countryName} (
                                  {country.countryMobileCode})
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        )}
                      />
                    </FormControl>
                    <FieldDescription text="The country where this plan will be available." />
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Section 4: Offer Configuration - EDITABLE */}
            <Accordion
              expanded={expandedSection === "offer"}
              onChange={handleAccordionChange("offer")}
              sx={{
                mb: 2,
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 1,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ bgcolor: "primary.50" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="primary.main"
                >
                  🎁 Offer Configuration (Editable)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Link this plan to another plan to create a discounted offer.
                </Alert>
                <Stack spacing={2}>
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>Link to Existing Plan</InputLabel>
                      <Controller
                        name="linkedPlanId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Link to Existing Plan"
                            value={field.value || ""}
                          >
                            <MenuItem value="">
                              <em>None (No linked plan)</em>
                            </MenuItem>
                            {isPlansLoading ? (
                              <MenuItem disabled>
                                <CircularProgress size={24} />
                              </MenuItem>
                            ) : (
                              existingPlans.map((plan) => (
                                <MenuItem key={plan.id} value={plan.id}>
                                  {plan.name} - {plan.amount} {plan.currency}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        )}
                      />
                    </FormControl>
                    <FieldDescription text="Select an existing plan to show as the 'original price'." />
                  </Box>

                  {watchLinkedPlanId && (
                    <Box>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Controller
                          name="offerValidTill"
                          control={control}
                          render={({ field }) => (
                            <DatePicker
                              label="Offer Valid Till"
                              value={field.value ? moment(field.value) : null}
                              onChange={(date) => field.onChange(date)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                },
                              }}
                              minDate={moment()}
                            />
                          )}
                        />
                      </LocalizationProvider>
                      <FieldDescription text="The date until which this offer price is valid." />
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Section 5: Usage Limits - EDITABLE */}
            <Accordion
              expanded={expandedSection === "limits"}
              onChange={handleAccordionChange("limits")}
              sx={{
                mb: 2,
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 1,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ bgcolor: "primary.50" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="primary.main"
                >
                  📊 Usage Limits (Editable)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Copy from existing plan */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: "primary.50",
                    border: "1px dashed",
                    borderColor: "primary.main",
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ContentCopy color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="primary.main">
                        Copy Usage Limits from Existing Plan
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Select an existing plan to automatically fill all usage
                      limit values. You can modify them after copying.
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={existingPlans}
                        getOptionLabel={(option) =>
                          `${option.name} (${option.type || "N/A"})`
                        }
                        value={selectedPlanForLimits}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            handleCopyUsageLimits(newValue);
                          } else {
                            setSelectedPlanForLimits(null);
                          }
                        }}
                        loading={isPlansLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Plan to Copy From"
                            placeholder="Search plans..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isPlansLoading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Type: {option.type || "N/A"} | Amount:{" "}
                                {option.amount
                                  ? `${option.amount / 100} ${option.currency}`
                                  : "Free"}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    </Stack>
                    {selectedPlanForLimits && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        Limits copied from:{" "}
                        <strong>{selectedPlanForLimits.name}</strong>
                      </Alert>
                    )}
                  </Stack>
                </Paper>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Set limits for each feature. Leave empty for unlimited usage.
                  Token limits control how much AI processing a user can use.
                  Conversation limits control how many separate conversations
                  they can have.
                </Alert>

                <Grid container spacing={2}>
                  {/* Smart Eye Limits */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      👁️ Smart Eye
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("smarteyeTokenLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum AI tokens per billing cycle" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("smarteyeConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum conversations allowed" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Short Token Limit"
                      {...register("smarteyeTokenLimitShort", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Tokens for quick responses" />
                  </Grid>

                  {/* Smart Eye Currency Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      💰 Smart Eye Currency
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("smarteyeCurrencyTokenLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum tokens for currency recognition" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("smarteyeCurrencyConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum currency recognition conversations" />
                  </Grid>

                  {/* Smart Image Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      🖼️ Smart Image
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("smartImageTokenLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum AI tokens for image processing" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("smartImageConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum image conversations" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Short Token Limit"
                      {...register("smartImageTokenLimitShort", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Tokens for quick image responses" />
                  </Grid>

                  {/* AI Chat Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      🤖 AI Chat
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("aiChatTokenLimit", { valueAsNumber: true })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum AI tokens for chat" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("aiChatConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum chat conversations" />
                  </Grid>

                  {/* MIRA Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      📚 MIRA
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Token Limit"
                      {...register("miraTokenLimit", { valueAsNumber: true })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum tokens for MIRA" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Conversation Limit"
                      {...register("miraConversationLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum MIRA conversations" />
                  </Grid>

                  {/* Document AI Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      📑 Document AI
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Ask Q&A Limit"
                      {...register("documentAskQnaLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum document Q&A queries" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Summary Limit"
                      {...register("documentSummaryLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum document summaries" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Generate Q&A Limit"
                      {...register("documentGenQnaLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum generated Q&A sets" />
                  </Grid>

                  {/* Other Limits */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      📄 Other Limits
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Text Limit"
                      {...register("textLimit", { valueAsNumber: true })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Text recognitions" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Document Pages Limit"
                      {...register("documentPagesLimit", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Maximum document pages" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Duration Days"
                      {...register("durationDays", {
                        valueAsNumber: true,
                      })}
                      placeholder="Leave empty for unlimited"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FieldDescription text="Plan duration in days" />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Section 6: Feature Access - EDITABLE */}
            <Accordion
              expanded={expandedSection === "features"}
              onChange={handleAccordionChange("features")}
              sx={{
                mb: 2,
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 1,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ bgcolor: "primary.50" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="primary.main"
                >
                  ⚙️ Feature Access Configuration (Editable)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Copy from existing plan */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: "secondary.50",
                    border: "1px dashed",
                    borderColor: "secondary.main",
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ContentCopy color="secondary" fontSize="small" />
                      <Typography variant="subtitle2" color="secondary.main">
                        Copy Feature Access from Existing Plan
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Select an existing plan to automatically copy all feature
                      access settings. You can modify them after copying.
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={existingPlans}
                        getOptionLabel={(option) =>
                          `${option.name} (${option.type || "N/A"})`
                        }
                        value={selectedPlanForFeatures}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            handleCopyFeatureAccess(newValue);
                          } else {
                            setSelectedPlanForFeatures(null);
                          }
                        }}
                        loading={isPlansLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Plan to Copy From"
                            placeholder="Search plans..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isPlansLoading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Type: {option.type || "N/A"} | Features:{" "}
                                {option.availableFeatures
                                  ? Object.keys(option.availableFeatures).length
                                  : 0}{" "}
                                configured
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    </Stack>
                    {selectedPlanForFeatures && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        Feature access copied from:{" "}
                        <strong>{selectedPlanForFeatures.name}</strong>
                      </Alert>
                    )}
                  </Stack>
                </Paper>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Edit the JSON directly to configure feature access.
                </Alert>

                <TextField
                  fullWidth
                  multiline
                  rows={20}
                  value={jsonEditorValue}
                  onChange={(e) => {
                    setJsonEditorValue(e.target.value);
                    setJsonError("");
                  }}
                  error={Boolean(jsonError)}
                  helperText={jsonError}
                  placeholder='{"feature": {"capability": {"isOnline": true, "isOffline": false}}}'
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                    },
                  }}
                />
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyJson}
                    disabled={!jsonEditorValue.trim()}
                  >
                    Apply JSON
                  </Button>
                  <Button variant="outlined" onClick={handleFormatJson}>
                    Format JSON
                  </Button>
                  <Button variant="outlined" onClick={handleRefreshJson}>
                    Refresh from Form
                  </Button>
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  JSON Structure Reference:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: "grey.100" }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {`{
  "featureName": {
    "create": { "isOnline": true, "isOffline": false },
    "askQuestion": { "isOnline": true, "isOffline": true },
    "history": { "isOnline": true, "isOffline": false },
    "reader": { "isOnline": false, "isOffline": true },
    "genQuestion": { "isOnline": true, "isOffline": false }
  },
  "anotherFeature": {
    // ... same structure
  }
}

Available default features: ${FEATURES.map((f) => f.key).join(", ")}
Available capabilities: create, askQuestion, history, reader, genQuestion`}
                  </Typography>
                </Paper>
              </AccordionDetails>
            </Accordion>

            {/* Section 7: Application Type - DISABLED */}
            <Accordion
              expanded={expandedSection === "appType"}
              onChange={handleAccordionChange("appType")}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="bold">
                    📱 Application Type
                  </Typography>
                  <DisabledBadge />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Controller
                  name="applicationPlanType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field}>
                      {APPLICATION_TYPES.map((type) => (
                        <Paper
                          key={type.value}
                          sx={{
                            p: 2,
                            mb: 1,
                            bgcolor: "action.disabledBackground",
                          }}
                        >
                          <FormControlLabel
                            value={type.value}
                            control={<Radio disabled />}
                            label={
                              <Box>
                                <Typography variant="subtitle2">
                                  {type.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {type.description}
                                </Typography>
                              </Box>
                            }
                          />
                        </Paper>
                      ))}
                    </RadioGroup>
                  )}
                />
              </AccordionDetails>
            </Accordion>

            {/* Submit Button */}
            <Box sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                loading={isUpdating}
              >
                Update Plan
              </LoadingButton>
            </Box>
          </form>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
}
